import { MouseEvent } from 'react';
import { ButtonView, ButtonViewProps } from '@shared/components/View';
import { grid } from '@shared/styles';
import { MBIconV2, MBIconV2Name } from '../image';
import { useMenuContext } from './MenuContext';
import styles from './MenuIconButton.module.css';

/**
 * Menu.Button
 *
 * A pre-styled trigger button that toggles the menu. Shows an optional
 * down-arrow indicator and an active state while the menu is open. Use this
 * when you want a standard button trigger; use `Menu.Trigger` when you need to
 * wrap a custom element instead.
 */

interface MenuIconButtonProps extends Omit<ButtonViewProps, 'onClick' | 'children'> {
  icon?: MBIconV2Name;
  iconSize?: number;
  isOpen?: boolean;
}

export function MenuIconButton({
  icon = 'dots',
  iconSize = grid(3),
  isOpen: isOpenProp,
  className,
  style,
  ...rest
}: MenuIconButtonProps) {
  const { toggle, isOpen, anchorName } = useMenuContext();

  const onClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    toggle();
  };

  return (
    <ButtonView
      onClick={onClick}
      style={{ anchorName, ...style }}
      aria-label="Open menu"
      aria-haspopup="menu"
      aria-expanded={isOpenProp || isOpen || undefined}
      className={[styles.menuIconButton, className]}
      {...rest}
    >
      <MBIconV2 name={icon} size={iconSize} />
    </ButtonView>
  );
}
